import { type RefObject, useCallback, useEffect, useState } from "react";

/** How long fullscreen may take before the window fallback takes over. */
const FULLSCREEN_GRACE_MS = 1000;

/**
 * TV mode: the tally fullscreen on a venue screen (the Fullscreen API on the
 * tally's container). Where the browser refuses fullscreen (an embedded
 * view, a kiosk policy), the container fills the window instead (`window`:
 * a fixed layer over the app) — the screen still gets its TV view.
 *
 * While it is on, the root font size goes up (`html[data-tv]`, styles.css —
 * everything is in rem, so the whole page scales), the reads refresh every
 * 30 s (the caller's `refetchInterval`), and the view may rotate. Escape
 * leaves: the browser's own exit for real fullscreen (`fullscreenchange` is
 * the source of truth there), a key listener for the window fallback.
 *
 * The session keeps refreshing its token on its own (lib/api/client.ts), so
 * a screen left on for hours stays signed in.
 */
export function useTvMode(target: RefObject<HTMLElement | null>): { on: boolean; window: boolean; enter: () => void; exit: () => void } {
	const [full, setFull] = useState(false);
	const [inWindow, setInWindow] = useState(false);
	const on = full || inWindow;

	useEffect(() => {
		const sync = () => setFull(document.fullscreenElement !== null && document.fullscreenElement === target.current);
		document.addEventListener("fullscreenchange", sync);
		return () => document.removeEventListener("fullscreenchange", sync);
	}, [target]);

	useEffect(() => {
		if (on) document.documentElement.setAttribute("data-tv", "");
		else document.documentElement.removeAttribute("data-tv");
		return () => document.documentElement.removeAttribute("data-tv");
	}, [on]);

	useEffect(() => {
		if (!inWindow) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape" && !e.defaultPrevented) setInWindow(false);
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [inWindow]);

	const enter = useCallback(() => {
		const el = target.current;
		if (!el || typeof el.requestFullscreen !== "function") {
			setInWindow(true);
			return;
		}
		void el.requestFullscreen().catch(() => setInWindow(true));
		// Some embedded browsers neither grant nor refuse: no fullscreen a second later is a refusal.
		setTimeout(() => {
			if (document.fullscreenElement !== el) setInWindow(true);
		}, FULLSCREEN_GRACE_MS);
	}, [target]);
	const exit = useCallback(() => {
		if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
		setInWindow(false);
	}, []);

	return { on, window: inWindow, enter, exit };
}
